import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';
import { dossiers } from '@/mocks/dossiers';
import { dimensions } from '@/mocks/dimensions';
import { site } from '@/mocks/site';
import DossierCard from '@/components/feature/DossierCard';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

type SortKey = 'latest' | 'hot';

export default function Dossiers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeDimension = searchParams.get('dimension') ?? 'all';
  const [keyword, setKeyword] = useState(searchParams.get('q') ?? '');
  const [sort, setSort] = useState<SortKey>('latest');

  useSeo({
    title: '拆解档案库 - 爆料看点｜爆料背后细节与事件隐藏关联档案',
    description:
      '爆料看点拆解档案库，按爆料背后细节、被忽略的伏笔、时间线关键节点、事件隐藏关联四个维度分类，支持关键词检索，快速找到你关心的那条脉络。',
    keywords: '爆料看点,拆解档案,爆料背后细节,被忽略的伏笔,时间线关键节点,事件隐藏关联',
    path: '/dossiers',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '拆解档案库',
        description:
          '按四个维度分类整理的事件拆解档案合集，覆盖爆料背后细节、被忽略的伏笔、时间线关键节点与事件隐藏关联。',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: site.name, url: 'https://baoliaokandian.site/' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaokandian.site/' },
          { '@type': 'ListItem', position: 2, name: '拆解档案', item: 'https://baoliaokandian.site/dossiers' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '拆解档案列表',
        itemListElement: dossiers.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `https://baoliaokandian.site/dossier/${item.id}`,
        })),
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = dossiers.filter((item) => {
      const matchDimension = activeDimension === 'all' || item.dimension === activeDimension;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchDimension && matchKeyword;
    });
    return list.sort((a, b) =>
      sort === 'hot' ? b.heat - a.heat : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [activeDimension, keyword, sort]);

  const handleDimension = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') {
      next.delete('dimension');
    } else {
      next.set('dimension', id);
    }
    setSearchParams(next, { replace: true });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) {
      next.set('q', keyword.trim());
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 pt-8 md:px-8">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '拆解档案' }]} />
        </div>
        <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <span className="font-label text-xs tracking-[0.3em] text-primary-600">ARCHIVE / 档案库</span>
          <h1 className="mt-3 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            每一篇档案，都把一条爆料拆到可以核对
          </h1>
          <p className="mt-5 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            按维度筛选，或直接搜关键词。每篇档案都包含核心疑问、原始细节、时间线节点、伏笔清单与隐藏关联，读完之后你可以自己复现整条路径。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-5 border-b border-background-200 pb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
                <button
                  type="button"
                  onClick={() => handleDimension('all')}
                  className={`whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                    activeDimension === 'all'
                      ? 'bg-foreground-950 text-background-50'
                      : 'text-foreground-700 hover:text-primary-600'
                  }`}
                >
                  全部
                </button>
                {dimensions.map((dimension) => (
                  <button
                    key={dimension.id}
                    type="button"
                    onClick={() => handleDimension(dimension.id)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                      activeDimension === dimension.id
                        ? 'bg-foreground-950 text-background-50'
                        : 'text-foreground-700 hover:text-primary-600'
                    }`}
                  >
                    <i className={dimension.icon} />
                    <span className="hidden sm:inline">{dimension.name}</span>
                    <span className="sm:hidden">{dimension.short}</span>
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <form onSubmit={handleSubmit} className="flex items-center gap-2">
                  <label htmlFor="archive-search" className="sr-only">
                    搜索档案
                  </label>
                  <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
                    <i className="ri-search-line text-base text-foreground-500" />
                    <input
                      id="archive-search"
                      type="search"
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                      placeholder="搜索标题、标签或摘要"
                      className="w-full min-w-[180px] bg-transparent font-body text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none sm:w-56"
                    />
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    搜索
                  </button>
                </form>

                <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
                  <button
                    type="button"
                    onClick={() => setSort('latest')}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                      sort === 'latest' ? 'bg-secondary-500 text-background-50' : 'text-foreground-700'
                    }`}
                  >
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort('hot')}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                      sort === 'hot' ? 'bg-secondary-500 text-background-50' : 'text-foreground-700'
                    }`}
                  >
                    最热
                  </button>
                </div>
              </div>
            </div>

            <p className="font-label text-xs text-foreground-500">
              共 <span className="text-primary-600">{filtered.length}</span> 篇档案
              {activeDimension !== 'all' && (
                <span> · 当前维度：{dimensions.find((item) => item.id === activeDimension)?.name}</span>
              )}
            </p>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((dossier, index) => (
                <DossierCard key={dossier.id} dossier={dossier} order={index} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-400" />
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有匹配的档案</h3>
              <p className="mt-2 font-body text-sm text-foreground-600">试试换个关键词，或切换到「全部」维度。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}